import React from 'react';

import { useDuplicateSitrepAsDraftV2 } from 'components/DisasterGPT/hooks/useSitrepsV2';
import type { SitrepSectionType } from 'components/DisasterGPT/hooks/useSitrepsV2';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import { SitrepCardSurface } from 'components/Reports/reports.types';
import ReadOnlyReportsSectionDisplay from 'components/Reports/ReadOnlyReportsSectionDisplay';
import SitrepCardShell from 'components/Reports/SitrepCardShell';
import SitrepSectionTabs from 'components/Reports/SitrepSectionTabs';
import useSitrepCardContentData from 'components/Reports/hooks/useSitrepCardContentData';
import useSitrepSectionSelector from 'components/Reports/hooks/useSitrepSectionSelector';

type SitrepSectionPanelProps = {
  sections: SitrepSectionType[] | undefined;
  activeSectionIndex: number | null;
  onSectionChange: (index: number) => void;
  tabSetId: string;
  activeSection: SitrepSectionType | null;
  children: React.ReactNode;
};

export const SitrepSectionPanel: React.FC<SitrepSectionPanelProps> = ({
  sections,
  activeSectionIndex,
  onSectionChange,
  tabSetId,
  activeSection,
  children,
}) => {
  if (!sections) return null;

  return (
    <>
      <SitrepSectionTabs
        sections={sections}
        activeSectionIndex={activeSectionIndex}
        onSectionChange={onSectionChange}
        className="mb-3"
        idBase={tabSetId}
      />

      {activeSection && (
        <div
          id={`${tabSetId}-panel-${activeSectionIndex}`}
          role="tabpanel"
          aria-labelledby={`${tabSetId}-tab-${activeSectionIndex}`}
          className="reports-card__section-content"
        >
          {children}
        </div>
      )}
    </>
  );
};

type SitrepCardContentProps = {
  sitrep: GetSitrepsResponse;
  surface: SitrepCardSurface;
  children?: React.ReactNode;
};

/**
 * Shared internal component for displaying SITREP card content. Used by both
 * CurrentReportsSitrepCard and ReadOnlySitrepCard.
 */
const SitrepCardContent: React.FC<SitrepCardContentProps> = ({
  sitrep,
  surface,
  children,
}) => {
  const tabSetId = React.useId();
  const cardData = useSitrepCardContentData(sitrep.id);
  const { sitrepData } = cardData;
  const { activeSection, activeSectionIndex, setActiveSectionIndex } =
    useSitrepSectionSelector(sitrepData?.sections);

  return (
    <SitrepCardShell sitrep={sitrep} surface={surface} cardData={cardData}>
      <SitrepSectionPanel
        sections={sitrepData?.sections}
        activeSectionIndex={activeSectionIndex}
        onSectionChange={setActiveSectionIndex}
        tabSetId={tabSetId}
        activeSection={activeSection}
      >
        <ReadOnlyReportsSectionDisplay section={activeSection!} />
      </SitrepSectionPanel>

      {children}
    </SitrepCardShell>
  );
};

type ReadOnlySitrepCardProps = {
  sitrep: GetSitrepsResponse;
};

/**
 * Read-only SITREP card without routing dependencies. Use this in contexts
 * without router (e.g., map popups, embeds). Renders sections directly without
 * the full card shell.
 */
export const ReadOnlySitrepCard: React.FC<ReadOnlySitrepCardProps> = ({
  sitrep,
}) => {
  const tabSetId = React.useId();
  const cardData = useSitrepCardContentData(sitrep.id);
  const { sitrepData, sectionsLoading } = cardData;
  const { activeSection, activeSectionIndex, setActiveSectionIndex } =
    useSitrepSectionSelector(sitrepData?.sections);

  if (sectionsLoading) {
    return <div className="p-3">Loading SITREP details...</div>;
  }

  return (
    <div className="reports-card__content">
      <h6 className="mb-3">
        {sitrep.status === 'Summarized' ? 'Current SITREP' : sitrep.status}
      </h6>

      <SitrepSectionPanel
        sections={sitrepData?.sections}
        activeSectionIndex={activeSectionIndex}
        onSectionChange={setActiveSectionIndex}
        tabSetId={tabSetId}
        activeSection={activeSection}
      >
        <ReadOnlyReportsSectionDisplay section={activeSection!} />
      </SitrepSectionPanel>
    </div>
  );
};

type CurrentReportsSitrepCardProps = {
  sitrep: GetSitrepsResponse;
  /**
   * When true, the "Draft New Version" button is hidden. Used for historical
   * views.
   */
  hideDraftButton?: boolean;
};

const CurrentReportsSitrepCard: React.FC<CurrentReportsSitrepCardProps> = ({
  sitrep,
  hideDraftButton = false,
}) => {
  const duplicate = useDuplicateSitrepAsDraftV2();
  const surface = hideDraftButton
    ? SitrepCardSurface.Historical
    : SitrepCardSurface.Current;

  return (
    <SitrepCardContent sitrep={sitrep} surface={surface}>
      {!hideDraftButton && (
        <div className="mt-3">
          <StylishNewButton
            className="button--primary"
            type="button"
            disabled={duplicate.isPending}
            onClick={async () => {
              await duplicate.mutateAsync({ sitrepId: sitrep.id });
            }}
          >
            Draft New Version
          </StylishNewButton>
        </div>
      )}
    </SitrepCardContent>
  );
};

export default CurrentReportsSitrepCard;
